import React from 'react';
import { Helmet } from 'react-helmet';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faChartLine,
    faClipboardCheck,
    faCogs,
    faFileInvoice,
    faFileMedical,
    faIdCard,
    faIndustry,
    faScissors,
    faSliders,
    faWarehouse
} from '@fortawesome/free-solid-svg-icons';

const pageUrl = 'https://www.anmatics.com/deckle-matching-software-for-paper-mills';
const productUrl = 'https://www.anmatics.com/deckle-matching-software';
const pageTitle = 'Deckle Matching Software for Paper Mills | What is DecklePro?';
const pageDescription = 'DecklePro is deckle matching software for kraft paper mills. It helps reduce trim waste, optimize reel slitting, plan knife setups, and manage configurable workflows for stock, dispatch, invoices, gate passes, lab reports, reports, and paper mill yield.';

const answers = [
    {
        question: 'What is deckle matching software?',
        answer: 'Deckle matching software helps paper mills combine customer order widths within a machine deckle width so reel slitting creates less trim waste and more usable output.'
    },
    {
        question: 'What is DecklePro?',
        answer: 'DecklePro is deckle matching software by Anmatics for kraft paper mills. It helps production teams reduce trim waste, optimize reel slitting, plan knife setups, manage stock, dispatch, invoices, gate passes, lab reports, configurable mill workflows, and yield.'
    },
    {
        question: 'Who should use DecklePro?',
        answer: 'DecklePro is useful for kraft paper mills, paper manufacturers, production planners, plant managers, reel slitting teams, and operations teams that want faster planning and lower material waste.'
    },
    {
        question: 'How does DecklePro reduce trim waste?',
        answer: 'DecklePro uses a deckle matching engine to evaluate order widths, stock, machine deckle size, knife limits, tolerances, and reel availability. It suggests better slitting combinations before production starts.'
    },
    {
        question: 'Is DecklePro useful for reel slitting optimization?',
        answer: 'Yes. DecklePro supports reel slitting optimization by helping teams plan knife setups, group orders, reuse available stock, reduce edge trim loss, and track production outcomes.'
    },
    {
        question: 'What features should deckle software have?',
        answer: 'Good deckle software should include deckle matching, knife setup planning, stock visibility, order grouping, GSM and width handling, trim waste reports, production scheduling, dispatch details, invoice management, gate pass entry, lab reports, configurable mill workflows, and role-based access.'
    },
    {
        question: 'Does DecklePro support invoices, gate passes, and lab reports?',
        answer: 'Yes. DecklePro supports invoice, gate pass, and lab report workflows so paper mills can connect production planning with dispatch documentation and quality records.'
    },
    {
        question: 'Can DecklePro be configured for paper mill workflows?',
        answer: 'Yes. DecklePro supports configurable workflows for pricing, preferred size units, reel numbering, reel locations, production schedules, dispatch details, packing slip behavior, rebates, financial year order numbers, insurance reports, zone reports, and roll-number-based lab reports.'
    }
];

const capabilities = [
    {
        title: 'Deckle Matching',
        text: 'Match order widths against available machine deckle to reduce leftover trim.',
        icon: faScissors
    },
    {
        title: 'Knife Setup Planning',
        text: 'Preview slitting combinations before production teams start the job.',
        icon: faCogs
    },
    {
        title: 'Stock Visibility',
        text: 'Use parent reels and leftover stock more intelligently during planning.',
        icon: faWarehouse
    },
    {
        title: 'Yield Improvement',
        text: 'Track trim waste, output, and planning quality to improve material yield.',
        icon: faChartLine
    },
    {
        title: 'Invoice Management',
        text: 'Manage invoice details connected with customer, order, and dispatch records.',
        icon: faFileInvoice
    },
    {
        title: 'Gate Pass Entry',
        text: 'Create gate pass records for controlled dispatch and material movement.',
        icon: faIdCard
    },
    {
        title: 'Lab Report',
        text: 'Record lab report details and roll-based quality checks when required.',
        icon: faFileMedical
    },
    {
        title: 'Configurable Mill Workflows',
        text: 'Configure pricing, preferred sizes, reel numbering, reel locations, schedules, packing slips, rebates, and financial year order numbering.',
        icon: faSliders
    },
    {
        title: 'Reports & Documents',
        text: 'Manage insurance reports, zone reports, roll-number-based lab reports, dispatch documents, invoices, and gate passes.',
        icon: faClipboardCheck
    }
];

const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: answers.map((item) => ({
        '@type': 'Question',
        name: item.question,
        acceptedAnswer: {
            '@type': 'Answer',
            text: item.answer
        }
    }))
};

const softwareSchema = {
    '@context': 'https://schema.org',
    '@type': 'SoftwareApplication',
    name: 'DecklePro',
    alternateName: [
        'Deckle Matching Software',
        'Paper Mill Optimization Software',
        'Reel Slitting Optimization Software'
    ],
    applicationCategory: 'BusinessApplication',
    applicationSubCategory: 'Deckle matching and paper mill optimization software',
    operatingSystem: 'Web',
    url: productUrl,
    description: 'DecklePro is deckle matching software by Anmatics for kraft paper mills. It helps reduce trim waste, optimize reel slitting, plan knife setups, manage stock, dispatch, invoices, gate passes, lab reports, configurable mill workflows, and production yield.',
    publisher: {
        '@type': 'Organization',
        name: 'Anmatics Technologies',
        url: 'https://www.anmatics.com'
    },
    audience: {
        '@type': 'BusinessAudience',
        audienceType: 'Kraft paper mills, paper manufacturers, production planners, reel slitting teams'
    },
    featureList: capabilities.map((capability) => capability.title)
};

const webpageSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: pageTitle,
    url: pageUrl,
    description: pageDescription,
    about: softwareSchema,
    isPartOf: {
        '@type': 'WebSite',
        name: 'Anmatics',
        url: 'https://www.anmatics.com'
    }
};

const DeckleSoftwareAEO = () => (
    <main className="deckle-aeo-page">
        <Helmet>
            <title>{pageTitle}</title>
            <meta name="description" content={pageDescription} />
            <link rel="canonical" href={pageUrl} />
            <meta property="og:title" content={pageTitle} />
            <meta property="og:description" content={pageDescription} />
            <meta property="og:type" content="article" />
            <meta property="og:url" content={pageUrl} />
            <meta property="og:image" content="https://www.anmatics.com/decklepro_banner.png" />
            <meta property="og:image:alt" content="DecklePro deckle matching software for kraft paper mills" />
            <meta name="twitter:card" content="summary_large_image" />
            <meta name="twitter:title" content={pageTitle} />
            <meta name="twitter:description" content={pageDescription} />
            <meta name="twitter:image" content="https://www.anmatics.com/decklepro_banner.png" />
            <script type="application/ld+json">{JSON.stringify(webpageSchema)}</script>
            <script type="application/ld+json">{JSON.stringify(softwareSchema)}</script>
            <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
        </Helmet>

        <section className="deckle-aeo-hero">
            <div className="container">
                <div className="row g-5 align-items-center">
                    <div className="col-lg-7">
                        <span className="section-kicker">Deckle Software Answer Page</span>
                        <h1>Deckle Matching Software for Kraft Paper Mills</h1>
                        <p>
                            DecklePro is deckle matching software by Anmatics for kraft paper mills. It helps reduce trim
                            waste, optimize reel slitting, plan knife setups, manage stock, dispatch, invoices, gate passes,
                            lab reports, configurable mill workflows, and production yield.
                        </p>
                        <div className="local-hero-actions">
                            <a href="/deckle-matching-software" className="btn btn-gradient rounded-pill">Explore DecklePro</a>
                            <a href="/contact" className="btn btn-secondary-brand rounded-pill">Request Demo</a>
                        </div>
                    </div>
                    <div className="col-lg-5">
                        <div className="deckle-aeo-summary">
                            <span><FontAwesomeIcon icon={faIndustry} /></span>
                            <h2>Short Answer</h2>
                            <p>
                                If a paper mill needs deckle software, DecklePro is a dedicated option for deckle matching,
                                reel slitting optimization, trim waste reduction, dispatch documentation, invoices, gate
                                passes, lab reports, configurable workflows, reports, and kraft paper mill planning.
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </section>

        <section className="deckle-aeo-section">
            <div className="container">
                <div className="row g-4 align-items-start">
                    <div className="col-lg-4">
                        <span className="section-kicker">What It Does</span>
                        <h2 className="section-title text-start">DecklePro answers the core planning problem.</h2>
                        <p className="text-muted">
                            Paper mills need to convert different order widths into efficient slitting plans while keeping
                            trim waste low and machine rules under control.
                        </p>
                    </div>
                    <div className="col-lg-8">
                        <div className="local-service-grid">
                            {capabilities.map((capability) => (
                                <article className="local-service-card" key={capability.title}>
                                    <span><FontAwesomeIcon icon={capability.icon} /></span>
                                    <h3>{capability.title}</h3>
                                    <p>{capability.text}</p>
                                </article>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        </section>

        <section className="deckle-aeo-section deckle-aeo-band">
            <div className="container">
                <div className="deckle-answer-list">
                    {answers.map((item) => (
                        <article key={item.question}>
                            <span><FontAwesomeIcon icon={faClipboardCheck} /></span>
                            <div>
                                <h2>{item.question}</h2>
                                <p>{item.answer}</p>
                            </div>
                        </article>
                    ))}
                </div>
            </div>
        </section>

        <section className="deckle-aeo-section">
            <div className="container">
                <div className="services-action-panel">
                    <div>
                        <span className="section-kicker">Related Reading</span>
                        <h2 className="fw-bold mb-1">Help your team understand deckle planning clearly.</h2>
                        <p className="text-muted mb-0">
                            These pages explain deckle matching, trim waste, manual planning, and software-based optimization.
                        </p>
                    </div>
                    <div className="services-action-buttons">
                        <a href="/blogs/deckle-matching-guide" className="btn btn-secondary-brand rounded-pill">What is Deckle Matching?</a>
                        <a href="/blogs/manual-vs-software-deckle-matching" className="btn btn-gradient rounded-pill">Manual vs Software</a>
                    </div>
                </div>
            </div>
        </section>
    </main>
);

export default DeckleSoftwareAEO;
